import React, { Dispatch, SetStateAction, useState } from "react";
import { Button, Image, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./Friends.scss";
import useDisclosure from "../../hooks/useDisclosure";
import DownloadAppPrompt, {
  PROMPT_DOWNLOAD_SCENARIOS,
  PromptDownloadScenariosType,
} from "../../components/DownloadAppPrompt/DownloadAppPrompt";
import PrimaryActionButton from "../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import SecondaryActionButton from "../../components/Buttons/SecondaryActionButton/SecondaryActionButton";
import ImageFacePileRandom from "../../images/face-pile-random.png";
import useAuth from "../../hooks/useAuth";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../utils/renderHeaders";
import { usePlayGameMetaDescription } from "../../utils/constants";
import ArrowRightIcon, {
  ArrowRightIconVariants,
} from "../../components/icons/ArrowRightIcon";
import HowItWorksModal from "../../components/friends/HowItWorksModal/HowItWorksModal";
import { FunctionWithVoidReturn } from "../../utils/constants/generic-types";

interface PlayWithFriendsProps {
  userIsLoggedIn: boolean;
  openDownloadAppPrompt: FunctionWithVoidReturn;
  setPromptDownloadScenario: Dispatch<
    SetStateAction<PromptDownloadScenariosType | undefined>
  >;
}

function PlayWithFriends({
  userIsLoggedIn,
  openDownloadAppPrompt,
  setPromptDownloadScenario,
}: PlayWithFriendsProps) {
  const { t } = useTranslation("friends");
  const {
    isOpen: isHowItWorksModalOpen,
    onOpen: openHowItWorksModal,
    onClose: closeHowItWorksModal,
  } = useDisclosure();

  const createGameLink = () => {
    setPromptDownloadScenario(
      userIsLoggedIn
        ? PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.PLAY_WITH_FRIENDS
        : PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.PLAY_WITH_FRIENDS
    );
    openDownloadAppPrompt();
  };

  return (
    <>
      <div className="PlayWithFriends">
        <div>
          <Image src={ImageFacePileRandom} alt="" width={237} height={112} />
        </div>
        <Stack gap={4} className="CopyWithCta">
          <Stack gap={2}>
            <h3 className="fs-6 Hanson-700 SpecialFontSettings MainCopy">
              {t("play_games_with_your_friends")}
            </h3>
            <p className="Grotesk SpecialFontSettings SubCopy">
              {t(
                "create_a_custom_game_link_and_share_with_your_friends_your_game_your_reward"
              )}
            </p>
          </Stack>
          <Stack className="align-items-center Ctas">
            <PrimaryActionButton
              text={t("create_game_link")}
              onClick={createGameLink}
            />
            <SecondaryActionButton
              text={t("how_it_works.how_it_works")}
              rightImage={
                <ArrowRightIcon
                  variant={ArrowRightIconVariants.THIN}
                  stroke="#6C53E8"
                />
              }
              buttonClassName="HowItWorks"
              onClick={openHowItWorksModal}
            />
          </Stack>
        </Stack>
      </div>

      <HowItWorksModal
        isOpen={isHowItWorksModalOpen}
        onClose={closeHowItWorksModal}
      />
    </>
  );
}

export default function Friends() {
  const [promptDownloadScenario, setPromptDownloadScenario] =
    useState<PromptDownloadScenariosType>();
  const { t } = useTranslation("friends");
  const [tabIndex, setTabIndex] = useState<number>(0);
  const { isLoggedIn: userIsLoggedIn } = useAuth();
  const metaDescriptions = usePlayGameMetaDescription();
  const {
    isOpen: isDownloadAppPromptOpen,
    onOpen: openDownloadAppPrompt,
    onClose: closeDownloadAppPrompt,
  } = useDisclosure();

  const handleTabChange = (newTabIndex: number) => {
    if (newTabIndex === 1) {
      setPromptDownloadScenario(
        userIsLoggedIn
          ? PROMPT_DOWNLOAD_SCENARIOS.LOGGED_IN.OTHERS
          : PROMPT_DOWNLOAD_SCENARIOS.LOGGED_OUT.OTHERS
      );
      openDownloadAppPrompt();
      return;
    }
    setTabIndex(newTabIndex);
  };

  return (
    <>
      {renderHeaders({
        title: t(
          "metadata:play_friends_accept_and_play_games_from_friends_and_people_you_know_hyper_gaming_app"
        ),
        description: metaDescriptions.playGames,
        ogTitle: extractOgTitleFromMainTitle(
          t(
            "metadata:play_friends_accept_and_play_games_from_friends_and_people_you_know_hyper_gaming_app"
          )
        ),
      })}

      <div className="FriendsPage">
        <Stack gap={4} className="py-4 px-3 Tabs">
          <Stack direction="horizontal" gap={4} className="p-1 TabList">
            <Button
              className={`Grotesk SpecialFontSettings Tab ${
                tabIndex === 0 ? "active" : undefined
              }`}
              onClick={() => handleTabChange(0)}
            >
              {t("common:play_with_friends")}
            </Button>
            <Button
              className={`Grotesk Tab ${tabIndex === 1 ? "active" : undefined}`}
              onClick={() => handleTabChange(1)}
            >
              {t("referrals")}
            </Button>
          </Stack>
          <div className="TabPanels">
            {tabIndex === 0 && (
              <div className="TabPanel">
                <PlayWithFriends
                  userIsLoggedIn={userIsLoggedIn}
                  openDownloadAppPrompt={openDownloadAppPrompt}
                  setPromptDownloadScenario={setPromptDownloadScenario}
                />
              </div>
            )}
          </div>
        </Stack>

        <DownloadAppPrompt
          isOpen={isDownloadAppPromptOpen}
          onOpen={openDownloadAppPrompt}
          onClose={closeDownloadAppPrompt}
          promptDownloadScenario={promptDownloadScenario}
        />
      </div>
    </>
  );
}
